import { ReactNode } from 'react'
import { useAuth } from '../hooks/useAuth'
import { Permission, UserRole } from '../types/auth'

interface RoleBasedComponentProps {
  children: ReactNode
  requiredPermission?: Permission
  requiredRole?: UserRole
  fallback?: ReactNode
  allowedRoles?: UserRole[]
}

export function RoleBasedComponent({ 
  children, 
  requiredPermission, 
  requiredRole,
  allowedRoles,
  fallback = null 
}: RoleBasedComponentProps) {
  const { user, hasPermission } = useAuth()

  if (!user) {
    return <>{fallback}</>
  }

  // Check specific permission
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <>{fallback}</>
  }

  // Check specific role
  if (requiredRole && user.role !== requiredRole) {
    return <>{fallback}</>
  }

  // Check if user role is in allowed roles
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <>{fallback}</>
  }

  return <>{children}</>
}

// Convenience components for common role checks
export function AdminOnly({ children, fallback }: { children: ReactNode; fallback?: ReactNode }) {
  return (
    <RoleBasedComponent allowedRoles={['OWNER', 'ADMIN']} fallback={fallback}>
      {children}
    </RoleBasedComponent>
  )
}

export function OwnerOnly({ children, fallback }: { children: ReactNode; fallback?: ReactNode }) {
  return (
    <RoleBasedComponent requiredRole="OWNER" fallback={fallback}>
      {children}
    </RoleBasedComponent>
  )
}

export function MemberOnly({ children, fallback }: { children: ReactNode; fallback?: ReactNode }) {
  return (
    <RoleBasedComponent requiredRole="MEMBER" fallback={fallback}>
      {children}
    </RoleBasedComponent>
  )
}